import React, { useState, useEffect } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Terms Data                                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

interface TermsSection {
  id: string;
  title: string;
  icon: string;
  content: string[];
}

const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'general',
    title: 'General Terms of Use',
    icon: 'zmdi-file-text',
    content: [
      'By accessing and using this website, you accept and agree to be bound by the terms and conditions set out below. If you do not agree to these terms, please do not use our website or services.',
      'These terms apply to all visitors, customers, and users of our website and services. We reserve the right to update or modify these terms at any time without prior notice. Your continued use of the website constitutes acceptance of any changes.',
      'All content on this website, including text, images, product descriptions, and pricing, is provided for general information purposes. While we strive for accuracy, we do not guarantee that all information is completely up to date at all times.',
      'You must be at least 18 years of age to place an order on our website. By placing an order, you confirm that you meet this age requirement.',
      'Unauthorised use of this website may give rise to a claim for damages and/or constitute a criminal offence. You agree not to use the site for any unlawful or prohibited purpose.',
    ],
  },
  {
    id: 'products',
    title: 'Products & Pricing',
    icon: 'zmdi-lamp',
    content: [
      'Our furniture pieces are crafted with care and may exhibit slight natural variations in wood grain, texture, tone, and finish depending on the material and production batch. These are characteristics of quality furniture craftsmanship and are not considered defects.',
      'All materials, dimensions, and care instructions are clearly stated on each product listing. We recommend reviewing product specifications carefully — including dimensions — before placing your order to ensure the piece fits your intended space.',
      'Product images are as accurate as possible; however, colour and finish rendering may vary depending on your display settings and screen calibration. We recommend referring to the product description for precise material and finish details.',
      'Prices are displayed in the currency shown on the website and are inclusive of applicable taxes unless otherwise stated. Prices are subject to change without notice and may vary during promotional periods.',
      'We reserve the right to limit quantities of any product and to discontinue any item at any time. Promotional prices and offers are valid only for the stated period and while stocks last.',
      'Custom and bespoke furniture orders are quoted individually. A written quotation will be provided before any work begins, and a deposit of at least 50% of the total value is required to initiate custom commissions. Lead times for bespoke orders will be communicated at the time of quotation.',
    ],
  },
  {
    id: 'orders',
    title: 'Orders & Contract',
    icon: 'zmdi-shopping-cart',
    content: [
      'Placing an item in your cart or submitting an order does not constitute a binding contract. A contract is formed only when we send you an order confirmation email confirming that your order has been accepted and is being processed.',
      'We reserve the right to refuse or cancel any order at our sole discretion, including orders that appear fraudulent, orders with incorrect pricing due to technical errors, or orders for items that have become unavailable or out of stock.',
      'If we are unable to fulfil your order after payment has been received, we will notify you promptly and arrange a full refund.',
      'You are responsible for ensuring that all details provided at checkout — including your delivery address, contact information, and access requirements — are accurate. We cannot be held liable for delivery failures or additional charges resulting from incorrect or incomplete information.',
      'Prior to delivery, please ensure that your property has adequate access, doorway clearance, and space to receive the furniture. We recommend measuring your space and access routes carefully before placing your order. Additional charges may apply if a delivery cannot be completed due to access issues.',
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Security',
    icon: 'zmdi-card',
    content: [
      'All payments are processed through secure, PCI-DSS compliant payment gateways. We accept major credit and debit cards, PayPal, and bank transfers. Staged payment options may be available for higher-value orders — please contact us for details.',
      'We do not store your card details on our servers at any point. All financial transactions are encrypted using SSL technology.',
      'In the event of a chargeback or payment dispute raised without first contacting our support team, we reserve the right to suspend your account pending investigation.',
      'For bank transfers, your order will only be processed once cleared funds have been received. Please allow 2–3 business days for bank transfer payments to clear.',
      'Prices quoted in foreign currencies are for reference only. Your payment provider will apply their exchange rate at the time of the transaction.',
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping & Delivery',
    icon: 'zmdi-truck',
    content: [
      'All furniture orders are carefully packaged and handled to ensure your items arrive in perfect condition. Larger items are delivered by our specialist furniture delivery teams, who are trained to handle bulky and fragile pieces with care.',
      'Delivery timeframes are estimates and may be affected by stock availability, production lead times, customs clearance, public holidays, and carrier delays beyond our control. We will keep you informed of any significant delays.',
      'International orders may be subject to import duties, taxes, and customs fees levied by the destination country. These charges are the sole responsibility of the recipient and are not included in our prices or shipping fees.',
      'Title and risk of loss for products pass to you upon delivery. Please inspect all items carefully at the point of delivery and note any visible damage on the delivery documentation before signing. Contact us immediately if damage is discovered.',
      'White-glove delivery and in-home assembly services are available for select items and locations. These services must be selected and paid for at the time of order. Additional charges may apply based on your location and the complexity of assembly.',
      'We do not deliver to P.O. boxes. A valid physical address and an authorised adult present at the time of delivery are required for all furniture orders.',
    ],
  },
  {
    id: 'returns',
    title: 'Returns & Refunds',
    icon: 'zmdi-refresh-alt',
    content: [
      'We accept returns within 14 days of delivery for items that are unused, unassembled, and in their original packaging, accompanied by the original proof of purchase. Due to the nature of furniture, returns must be arranged through our customer support team.',
      'Custom-made, bespoke, and made-to-order furniture pieces are non-returnable unless they are faulty or not as described. Please review your order specifications carefully before confirming a custom order.',
      'To initiate a return, please contact our customer support team. Do not attempt to return items without prior authorisation. Unauthorised returns will not be accepted.',
      'Refunds are processed within 5–10 business days of us collecting and inspecting the returned item. Refunds will be issued via the original payment method.',
      'We do not refund original delivery charges unless the return is due to our error or a confirmed product defect. The cost of return collection for change-of-mind returns is borne by the customer.',
      'If an item shows signs of assembly, use, modification, or damage after delivery, we reserve the right to refuse the return or apply a restocking fee.',
    ],
  },
  {
    id: 'warranty',
    title: 'Quality & Guarantee',
    icon: 'zmdi-badge-check',
    content: [
      'All our furniture pieces are quality inspected before dispatch to ensure they meet our standards for structural integrity, finish quality, and material consistency.',
      'We offer a 12-month limited warranty against manufacturing defects from the date of delivery. This warranty does not cover damage resulting from normal wear and tear, misuse, accidents, exposure to moisture or direct sunlight, improper assembly, or unauthorised modifications.',
      'Warranty claims must be accompanied by proof of purchase and photographic evidence of the defect. We reserve the right to repair, replace, or refund the item at our discretion upon verification of the defect.',
      'To maintain the quality and longevity of your furniture, we recommend following the care and maintenance instructions provided with your product. Damage resulting from improper cleaning, excessive weight, or environmental factors is not covered under this warranty.',
    ],
  },
  {
    id: 'ip',
    title: 'Intellectual Property',
    icon: 'zmdi-copyright',
    content: [
      'All content on this website — including but not limited to furniture designs, photographs, room set imagery, text, logos, and graphics — is our intellectual property or that of our licensors and is protected by copyright and other applicable laws.',
      'You may not reproduce, distribute, display, or create derivative works from any content on this site without our express written permission.',
      'Our brand name, logo, and all original furniture designs and collections are trademarks of our business. Unauthorised use of our trademarks or design elements is strictly prohibited.',
      'If you believe any content on our website infringes your intellectual property rights, please contact us with full details and we will investigate promptly.',
    ],
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    icon: 'zmdi-alert-triangle',
    content: [
      'To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products, including any damage to property arising from improper assembly or installation.',
      'Our total liability in connection with any order shall not exceed the amount paid by you for that specific order.',
      'We make no warranty that the website will be uninterrupted, error-free, or free of viruses or other harmful components.',
      'These terms do not exclude or limit our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by applicable law.',
    ],
  },
  {
    id: 'governing',
    title: 'Governing Law',
    icon: 'zmdi-balance',
    content: [
      'These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which our business is registered.',
      'Any disputes arising under or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of that jurisdiction.',
      'If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.',
      'These terms constitute the entire agreement between you and us in relation to your use of this website and supersede all previous agreements.',
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const TermsAndConditions: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(TERMS_SECTIONS[0].id);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      for (const section of TERMS_SECTIONS) {
        const el = document.getElementById(`tc-section-${section.id}`);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(section.id);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`tc-section-${id}`);
    if (el) {
      const offset = 90;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Terms & Conditions</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Hero ── */}
          <div className="lp-hero">
            <div className="lp-hero__icon">
              <i className="zmdi zmdi-file-text"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Terms &amp; Conditions</h2>
            <p className="stext-113 cl6 lp-hero__sub">
              Please read these terms carefully before purchasing from our store. By placing an order, you agree to the conditions outlined below.
            </p>
            <p className="stext-113 cl6" style={{ fontSize: 12, marginTop: 6 }}>
              Last updated: June 2025 &nbsp;·&nbsp; {TERMS_SECTIONS.length} sections
            </p>
          </div>

          {/* ── Layout ── */}
          <div className="lp-layout">

            {/* Sticky sidebar nav */}
            <nav className="lp-nav">
              <p className="lp-nav__heading">Contents</p>
              {TERMS_SECTIONS.map((sec, i) => (
                <button
                  key={sec.id}
                  className={`lp-nav__item ${activeSection === sec.id ? 'lp-nav__item--active' : ''}`}
                  onClick={() => scrollTo(sec.id)}
                  type="button"
                >
                  <span className="lp-nav__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="lp-nav__label">{sec.title}</span>
                </button>
              ))}
            </nav>

            {/* Content */}
            <div className="lp-content">
              {TERMS_SECTIONS.map((sec, i) => (
                <div
                  key={sec.id}
                  id={`tc-section-${sec.id}`}
                  className="lp-section"
                >
                  <div className="lp-section__header">
                    <div className="lp-section__num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="lp-section__title-wrap">
                      <i className={`zmdi ${sec.icon} lp-section__icon`}></i>
                      <h4 className="lp-section__title">{sec.title}</h4>
                    </div>
                  </div>
                  <div className="lp-section__body">
                    {sec.content.map((para, j) => (
                      <p key={j} className="lp-section__para">{para}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer note */}
              <div className="lp-footnote">
                <i className="zmdi zmdi-info-outline" style={{ fontSize: 20, color: '#717fe0', marginBottom: 10, display: 'block' }}></i>
                <p className="stext-113 cl6">
                  If you have any questions about these Terms &amp; Conditions, please{' '}
                  <a href="/contact" className="lp-link">contact our team</a>. We are happy to clarify anything before you place your order.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .lp-hero {
          text-align: center;
          padding-bottom: 56px;
        }
        .lp-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          border: 1px solid #e0e0f8;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 28px;
          color: #717fe0;
        }
        .lp-hero__sub {
          max-width: 540px;
          margin: 0 auto 8px;
          line-height: 1.7;
        }

        /* ── Layout ── */
        .lp-layout {
          display: grid;
          grid-template-columns: 230px 1fr;
          gap: 48px;
          align-items: start;
        }

        /* ── Sidebar nav ── */
        .lp-nav {
          position: sticky;
          top: 24px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .lp-nav__heading {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: #bbb;
          margin-bottom: 10px;
          padding-left: 12px;
        }
        .lp-nav__item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          border: none;
          border-radius: 5px;
          background: none;
          cursor: pointer;
          text-align: left;
          width: 100%;
          font-family: Poppins-Regular, sans-serif;
          transition: background 0.2s;
          border-left: 2px solid transparent;
        }
        .lp-nav__item:hover {
          background: #f5f5ff;
        }
        .lp-nav__item--active {
          background: #f0f2ff;
          border-left-color: #717fe0;
        }
        .lp-nav__num {
          font-size: 10px;
          font-weight: 700;
          color: #ccc;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
          transition: color 0.2s;
        }
        .lp-nav__item--active .lp-nav__num { color: #717fe0; }
        .lp-nav__label {
          font-size: 12px;
          color: #777;
          line-height: 1.4;
          transition: color 0.2s;
        }
        .lp-nav__item--active .lp-nav__label { color: #333; font-weight: 500; }

        /* ── Content sections ── */
        .lp-section {
          margin-bottom: 52px;
          scroll-margin-top: 90px;
        }
        .lp-section__header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e0e0f8;
        }
        .lp-section__num {
          font-size: 12px;
          font-weight: 700;
          color: #717fe0;
          letter-spacing: 0.5px;
          line-height: 1;
          margin-top: 3px;
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
        }
        .lp-section__title-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lp-section__icon {
          font-size: 18px;
          color: #717fe0;
        }
        .lp-section__title {
          font-size: 16px;
          font-weight: 600;
          color: #2c2c2c;
          margin: 0;
        }
        .lp-section__body {
          padding-left: 0;
        }
        .lp-section__para {
          font-size: 13.5px;
          color: #555;
          line-height: 1.85;
          margin: 0 0 14px;
          padding-left: 26px;
          position: relative;
        }
        .lp-section__para::before {
          content: '';
          position: absolute;
          left: 10px;
          top: 10px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #717fe0;
        }
        .lp-section__para:last-child { margin-bottom: 0; }

        /* ── Footnote ── */
        .lp-footnote {
          background: #f8f9ff;
          border: 1px solid #dde2f8;
          border-radius: 8px;
          padding: 28px 28px;
          text-align: center;
          margin-top: 16px;
        }
        .lp-link {
          color: #717fe0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: color 0.2s;
        }
        .lp-link:hover { color: #4a55c8; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .lp-layout {
            grid-template-columns: 1fr;
          }
          .lp-nav {
            position: static;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 4px;
          }
          .lp-nav__heading { display: none; }
          .lp-nav__item {
            flex: 0 0 auto;
            padding: 6px 12px;
            border-left: none;
            border-bottom: 2px solid transparent;
          }
          .lp-nav__item--active {
            border-left-color: transparent;
            border-bottom-color: #717fe0;
          }
          .lp-nav__num { display: none; }
          .lp-nav__label { font-size: 11px; }
        }
        @media (max-width: 480px) {
          .lp-section__para {
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsAndConditions;